/**
 * 広告画像エディタ。
 * - 左パネル: Generate（生成設定）/ Design（要素編集）のタブ切替
 * - メイン: InteractiveCanvas（プレビュー + 直接編集）
 * - 下部: LayerPanel
 * - 上部 actions: PNG/JPG 出力ボタン
 */
import {
  findAdSizePreset,
  type AdElement,
  type AdImageConfig,
  type AdImageSlide,
} from '@goenhub/shared';
import {
  AlertTriangle,
  AlignCenterHorizontal,
  AlignCenterVertical,
  AlignEndHorizontal,
  AlignEndVertical,
  AlignHorizontalSpaceAround,
  AlignStartHorizontal,
  AlignStartVertical,
  AlignVerticalSpaceAround,
  Copy,
  Download,
  FileImage,
  Folder,
  FolderOpen,
  Hand,
  Locate,
  Loader2,
  Magnet,
  MousePointer2,
  PenTool,
  Redo2,
  Sparkles,
  Undo2,
  Wand2,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { AddElementMenu } from './components/AddElementMenu';
import { DesignTab } from './components/DesignTab';
import { GenerateTab } from './components/GenerateTab';
import { InteractiveCanvas, BACKGROUND_LAYER_ID } from './components/InteractiveCanvas';
import { LayerPanel } from './components/LayerPanel';
import { SlideTabs } from './components/SlideTabs';
import { VariantGenerateDialog } from './components/VariantGenerateDialog';
import { exportSlideAs } from './utils/exportImage';
import {
  alignUnits,
  collectForCopy,
  distributeUnits,
  pasteClipboard,
  resolveUnits,
  type AlignMode,
  type ElementClipboard,
} from './utils/groupUnits';

import { Screen } from '@/components/layout/Screen';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { toast } from '@/components/ui/toast';
import {
  patchSlide,
  patchSlideElement,
  useAdImage,
  useGenerateAdImage,
  useOptimizeAdImageText,
  useUpdateAdImage,
  useUpdateAdImageSlides,
} from '@/hooks/useAdImages';
import { useCustomFonts } from '@/hooks/useCustomFonts';
import { useHistory } from '@/hooks/useSlidesHistory';

type LeftTab = 'generate' | 'design';

export function AdImageEditorPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: adImage, isLoading } = useAdImage(id);
  // Phase 29.4: カスタムフォントを起動時に @font-face として注入
  useCustomFonts();

  const [leftTab, setLeftTab] = useState<LeftTab>('generate');
  const [activeSlideId, setActiveSlideId] = useState<string | null>(null);
  // Phase 38.3: 複数選択対応 — selectedIds[0] が「プライマリ選択」、複数選択中はそれ以降も
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  // Phase 38.4: グループ編集モード — 入っているグループ内の個別要素を選択可能
  const [activeGroupId, setActiveGroupId] = useState<string | null>(null);
  // 既存コードとの互換: 単一選択時の id（複数選択中は最初の値）
  const selectedElementId = selectedIds[0] ?? null;
  const setSelectedElementId = useCallback((id: string | null) => {
    setSelectedIds(id ? [id] : []);
    if (!id) setActiveGroupId(null);
  }, []);
  const [variantDialogOpen, setVariantDialogOpen] = useState(false);
  const [snapEnabled, setSnapEnabled] = useState(true);
  // Phase 32.4: ツール切替（cursor / pen）
  const [tool, setToolRaw] = useState<'cursor' | 'pen' | 'grab'>('cursor');
  /** Phase 35.1: ツール切替時に選択を解除 */
  const setTool = useCallback((t: 'cursor' | 'pen' | 'grab') => {
    setToolRaw(t);
    setSelectedElementId(null);
  }, []);
  /** Phase 36: ユーザーズーム倍率（0.25 - 2.0） */
  const [zoom, setZoom] = useState(1);
  // selectedIds 経由ヘルパは下記 handleSelectElementClick / handleGroupSelected で実装
  /** Phase 37: グラブツールのドラッグでスクロールするためのコンテナ ref */
  const canvasScrollRef = useRef<HTMLDivElement | null>(null);
  /** Phase 37: ビュー座標を中央にリセット */
  const recenterView = useCallback(() => {
    const el = canvasScrollRef.current;
    if (!el) return;
    el.scrollLeft = Math.max(0, (el.scrollWidth - el.clientWidth) / 2);
    el.scrollTop = Math.max(0, (el.scrollHeight - el.clientHeight) / 2);
  }, []);
  // Phase 32: クリップボード（同タブ内のみ）
  // グループ丸ごとのコピーに対応するため、要素とグループ定義をまとめて持つ
  const clipboardRef = useRef<ElementClipboard | null>(null);

  // ローカルの編集中ステート（debounce 保存）
  const [draftTitle, setDraftTitle] = useState('');
  const [draftConfig, setDraftConfig] = useState<AdImageConfig | null>(null);
  // Phase 32.1: undo/redo 履歴付きの slides 状態
  const slidesHistory = useHistory<AdImageSlide[]>([]);
  const draftSlides = slidesHistory.state;
  const setDraftSlides = slidesHistory.commit;

  const update = useUpdateAdImage();
  const updateSlides = useUpdateAdImageSlides();
  const generate = useGenerateAdImage();
  const optimize = useOptimizeAdImageText();

  // 取得直後にローカル状態へ同期（履歴リセット）
  useEffect(() => {
    if (!adImage) return;
    setDraftTitle(adImage.title);
    setDraftConfig(adImage.config);
    slidesHistory.reset(adImage.slides ?? []);
    if (!activeSlideId && adImage.slides?.[0]) {
      setActiveSlideId(adImage.slides[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adImage?.id]);

  // slides を編集したら debounce 保存
  const slidesSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!id || draftSlides.length === 0) return;
    if (slidesSaveTimer.current) clearTimeout(slidesSaveTimer.current);
    slidesSaveTimer.current = setTimeout(() => {
      updateSlides.mutate({ id, slides: draftSlides });
    }, 800);
    return () => {
      if (slidesSaveTimer.current) clearTimeout(slidesSaveTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftSlides, id]);

  // Phase 41: html-to-image によるサムネイル生成は Google Fonts 全 27 種類の埋め込みで
  // 30 秒以上ハングするため、専用のミニキャンバス描画方式に変更（AdImagesListPage 側で実装）。
  // 旧 thumbnailSaveTimer ロジックは廃止。

  const activeSlide = useMemo(
    () => draftSlides.find((s) => s.id === activeSlideId) ?? null,
    [draftSlides, activeSlideId],
  );

  const selectedElement = useMemo<AdElement | null>(() => {
    if (!activeSlide || !selectedElementId) return null;
    if (selectedElementId === BACKGROUND_LAYER_ID) return null;
    return activeSlide.elements.find((e) => e.id === selectedElementId) ?? null;
  }, [activeSlide, selectedElementId]);

  const selectedBackgroundLayer = selectedElementId === BACKGROUND_LAYER_ID;

  const preset = useMemo(
    () => (draftConfig ? findAdSizePreset(draftConfig.sizePresetId) : null),
    [draftConfig],
  );

  /**
   * Phase 38.3 / 38.4: クリック時の選択処理
   * - Shift+クリック: 複数選択 (toggle)
   * - グループ所属要素のクリック: グループ全体を選択（activeGroupId が一致しないとき）
   * - 既に group 編集中なら個別要素を選択
   */
  const handleSelectElementClick = useCallback(
    (id: string | null, opts?: { shift?: boolean }) => {
      if (id === null) {
        setSelectedIds([]);
        setActiveGroupId(null);
        return;
      }
      if (id === BACKGROUND_LAYER_ID) {
        setSelectedIds([id]);
        setActiveGroupId(null);
        return;
      }
      const el = activeSlide?.elements.find((e) => e.id === id);
      const gid = el?.groupId;
      if (gid && gid !== activeGroupId) {
        const members = (activeSlide?.elements ?? [])
          .filter((e) => e.groupId === gid)
          .map((e) => e.id);
        if (opts?.shift) {
          setSelectedIds((prev) => {
            const allSelected = members.every((m) => prev.includes(m));
            return allSelected
              ? prev.filter((p) => !members.includes(p))
              : [...prev, ...members.filter((m) => !prev.includes(m))];
          });
        } else {
          setSelectedIds(members);
        }
        return;
      }
      if (opts?.shift) {
        setSelectedIds((prev) =>
          prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id],
        );
      } else {
        setSelectedIds([id]);
      }
    },
    [activeSlide, activeGroupId],
  );

  /** Phase 38.4: 選択中の要素群をグループ化 */
  const handleGroupSelected = useCallback(() => {
    if (!activeSlide || selectedIds.length < 2) return;
    const groupId =
      typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `g-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    const existingGroups = activeSlide.groups ?? [];
    const groupName = `グループ ${existingGroups.length + 1}`;
    setDraftSlides((prev) =>
      patchSlide(prev, activeSlide.id, {
        elements: activeSlide.elements.map((e) =>
          selectedIds.includes(e.id) ? { ...e, groupId } : e,
        ),
        groups: [...existingGroups, { id: groupId, name: groupName }],
      }),
    );
  }, [activeSlide, selectedIds, setDraftSlides]);

  /** Phase 38.4: 選択中の要素をグループから外す */
  const handleUngroupSelected = useCallback(() => {
    if (!activeSlide || selectedIds.length === 0) return;
    const primaryEl = activeSlide.elements.find((e) => e.id === selectedIds[0]);
    const gid = primaryEl?.groupId;
    if (!gid) return;
    const nextElements = activeSlide.elements.map((e) =>
      e.groupId === gid ? { ...e, groupId: undefined } : e,
    );
    const nextGroups = (activeSlide.groups ?? []).filter((g) => g.id !== gid);
    setDraftSlides((prev) =>
      patchSlide(prev, activeSlide.id, {
        elements: nextElements,
        groups: nextGroups,
      }),
    );
    setActiveGroupId((cur) => (cur === gid ? null : cur));
  }, [activeSlide, selectedIds, setDraftSlides]);

  /** Phase 38.4: グループ名変更 */
  const handleRenameGroup = useCallback(
    (groupId: string, name: string) => {
      if (!activeSlide) return;
      const nextGroups = (activeSlide.groups ?? []).map((g) =>
        g.id === groupId ? { ...g, name } : g,
      );
      setDraftSlides((prev) => patchSlide(prev, activeSlide.id, { groups: nextGroups }));
    },
    [activeSlide, setDraftSlides],
  );

  /**
   * Phase 40: 配置（揃え）
   * - 1 まとまり選択時: キャンバスを基準
   * - 複数選択時: レイヤー順位が最も上のまとまりを基準（基準は動かさず、他を揃える）
   * - グループは 1 まとまりとして外接矩形で揃え、メンバーは同じ量だけ動く（2026-09-25〜。
   *   以前は要素ごとに揃えていたためグループ内の配置が崩れた）。グループ編集中のメンバーは個別に扱う
   */
  const handleAlign = useCallback(
    (mode: AlignMode) => {
      if (!activeSlide || !preset) return;
      const targetIds = selectedIds.filter((sid) => sid !== BACKGROUND_LAYER_ID);
      const units = resolveUnits(activeSlide.elements, targetIds, activeGroupId);
      if (units.length === 0) return;
      const nextElements = alignUnits(activeSlide, units, mode, preset);
      setDraftSlides((prev) => patchSlide(prev, activeSlide.id, { elements: nextElements }));
    },
    [activeSlide, preset, selectedIds, activeGroupId, setDraftSlides],
  );

  /**
   * Phase 40: 等間隔分配
   * - 最も左（上）と右（下）のまとまりを端として、間のまとまりの中心点を等間隔に再配置
   * - グループは 1 まとまりとして扱う（2026-09-25〜）
   */
  const handleDistribute = useCallback(
    (axis: 'horizontal' | 'vertical') => {
      if (!activeSlide) return;
      const targetIds = selectedIds.filter((sid) => sid !== BACKGROUND_LAYER_ID);
      const units = resolveUnits(activeSlide.elements, targetIds, activeGroupId);
      if (units.length < 3) return;
      const nextElements = distributeUnits(activeSlide.elements, units, axis);
      setDraftSlides((prev) => patchSlide(prev, activeSlide.id, { elements: nextElements }));
    },
    [activeSlide, selectedIds, activeGroupId, setDraftSlides],
  );

  /** 整列・等間隔ボタンの表示判定用（グループは 1 まとまりで数える） */
  const selectionUnitCount = useMemo(
    () =>
      activeSlide
        ? resolveUnits(
            activeSlide.elements,
            selectedIds.filter((sid) => sid !== BACKGROUND_LAYER_ID),
            activeGroupId,
          ).length
        : 0,
    [activeSlide, selectedIds, activeGroupId],
  );

  const handleSaveMeta = () => {
    if (!id || !draftConfig) return;
    update.mutate({ id, data: { title: draftTitle, config: draftConfig } });
  };

  const handleGenerate = async () => {
    if (!id || !draftConfig) return;
    await update.mutateAsync({ id, data: { title: draftTitle, config: draftConfig } });
    try {
      const res = await generate.mutateAsync({ adImageId: id });
      slidesHistory.reset(res.slides);
      setActiveSlideId(res.slides[0]?.id ?? null);
      setSelectedElementId(null);
      setLeftTab('design');
      toast.success('生成しました');
    } catch (e) {
      toast.error(`生成失敗: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  const handlePatchElement = (elementId: string, patch: Partial<AdElement>) => {
    if (!activeSlide) return;
    setDraftSlides((prev) => patchSlideElement(prev, activeSlide.id, elementId, patch));
  };

  const handlePatchSlide = (patch: Partial<AdImageSlide>) => {
    if (!activeSlide) return;
    setDraftSlides((prev) => patchSlide(prev, activeSlide.id, patch));
  };

  const handleReorderLayers = (newOrder: string[]) => {
    if (!activeSlide) return;
    setDraftSlides((prev) => patchSlide(prev, activeSlide.id, { layerOrder: newOrder }));
  };

  const handleDeleteElement = useCallback(
    (elementId: string, opts?: { skipConfirm?: boolean }) => {
      if (!activeSlide) return;
      const target = activeSlide.elements.find((e) => e.id === elementId);
      if (!target) return;
      const label =
        target.kind === 'text'
          ? target.content.slice(0, 20)
          : target.kind === 'image_placeholder'
            ? target.label
            : 'この要素';
      if (!opts?.skipConfirm && !confirm(`「${label}」を削除します。よろしいですか？`)) return;
      setDraftSlides((prev) =>
        patchSlide(prev, activeSlide.id, {
          elements: activeSlide.elements.filter((e) => e.id !== elementId),
          layerOrder: (activeSlide.layerOrder ?? []).filter((id) => id !== elementId),
        }),
      );
      if (selectedElementId === elementId) setSelectedElementId(null);
    },
    [activeSlide, selectedElementId, setDraftSlides],
  );

  /**
   * Phase 32.3: Option+ドラッグでの複製。ドラッグ対象（選択中のまとまり。未選択要素を掴んだ場合は
   * その要素のまとまり）を元の位置に複製して残し、元はそのままドラッグで動かす。
   * グループ丸ごとの複製は別の新しいグループになる（2026-09-25〜。以前は同じグループに 1 要素だけ増えた）。
   */
  const handleDuplicateForDrag = useCallback(
    (elementId: string) => {
      if (!activeSlide) return;
      const baseIds = selectedIds.includes(elementId)
        ? selectedIds.filter((sid) => sid !== BACKGROUND_LAYER_ID)
        : [elementId];
      const clip = collectForCopy(activeSlide, baseIds, activeGroupId);
      if (!clip) return;
      const { slide: next } = pasteClipboard(activeSlide, clip, 0);
      setDraftSlides((prev) => prev.map((s) => (s.id === activeSlide.id ? next : s)));
    },
    [activeSlide, selectedIds, activeGroupId, setDraftSlides],
  );

  /** Phase 32: コピー。選択中のまとまり（グループは丸ごと）を記憶する */
  const handleCopySelected = useCallback(() => {
    if (!activeSlide) return;
    const clip = collectForCopy(
      activeSlide,
      selectedIds.filter((sid) => sid !== BACKGROUND_LAYER_ID),
      activeGroupId,
    );
    if (!clip) return;
    clipboardRef.current = clip;
    toast.success('コピーしました');
  }, [activeSlide, selectedIds, activeGroupId]);

  /** Phase 32: ペースト。グループ丸ごとのコピーは別の新しいグループとして貼り付ける */
  const handlePaste = useCallback(() => {
    if (!activeSlide || !clipboardRef.current) return;
    const clip = clipboardRef.current;
    const { slide: next, newIds } = pasteClipboard(activeSlide, clip, 20);
    setDraftSlides((prev) => prev.map((s) => (s.id === activeSlide.id ? next : s)));
    // 丸ごとのグループを貼った場合はグループ編集を抜けて、貼った内容を選択
    if (clip.wholeGroupIds.length > 0) setActiveGroupId(null);
    setSelectedIds(newIds);
  }, [activeSlide, setDraftSlides]);

  const handlePatchBackground = (patch: {
    x?: number;
    y?: number;
    w?: number;
    h?: number;
    fit?: 'cover' | 'contain' | 'fill';
    opacity?: number;
  }) => {
    if (!activeSlide || !activeSlide.background) return;
    setDraftSlides((prev) =>
      patchSlide(prev, activeSlide.id, {
        background: { ...activeSlide.background!, ...patch },
      }),
    );
  };

  const handleOptimize = async () => {
    if (!id || !activeSlide) return;
    try {
      const res = await optimize.mutateAsync({
        adImageId: id,
        slideId: activeSlide.id,
        elementId: selectedElementId ?? undefined,
      });
      setDraftSlides((prev) =>
        prev.map((s) => (s.id === res.slideId ? { ...s, elements: res.elements } : s)),
      );
      toast.success('テキストを最適化しました');
    } catch (e) {
      toast.error(`最適化失敗: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  // Phase 39: アローキーでの 1px / 10px (Shift) 移動。連打を 1 履歴ステップにまとめる
  const arrowNudgeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const arrowNudgeActiveRef = useRef(false);

  // Phase 32: グローバルキーボードショートカット
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName?.toLowerCase();
      // 入力欄では大半のショートカットを無効化（Esc は許可）
      const isEditingField = tag === 'input' || tag === 'textarea' || target?.isContentEditable;
      const mod = e.metaKey || e.ctrlKey;

      // Esc: 選択解除 + グループ編集モード解除
      if (e.key === 'Escape') {
        setSelectedElementId(null);
        setActiveGroupId(null);
        return;
      }
      if (isEditingField) return;

      // Undo / Redo
      if (mod && !e.shiftKey && (e.key === 'z' || e.key === 'Z')) {
        e.preventDefault();
        slidesHistory.undo();
        return;
      }
      if (
        mod &&
        (e.key === 'y' || e.key === 'Y' || (e.shiftKey && (e.key === 'z' || e.key === 'Z')))
      ) {
        e.preventDefault();
        slidesHistory.redo();
        return;
      }
      // Copy
      if (mod && (e.key === 'c' || e.key === 'C')) {
        if (selectedElementId && selectedElementId !== BACKGROUND_LAYER_ID) {
          e.preventDefault();
          handleCopySelected();
        }
        return;
      }
      // Paste
      if (mod && (e.key === 'v' || e.key === 'V')) {
        if (clipboardRef.current) {
          e.preventDefault();
          handlePaste();
        }
        return;
      }
      // Delete / Backspace
      if (e.key === 'Backspace' || e.key === 'Delete') {
        if (selectedElementId && selectedElementId !== BACKGROUND_LAYER_ID) {
          e.preventDefault();
          handleDeleteElement(selectedElementId, { skipConfirm: true });
        }
        return;
      }
      // Tool 切替: V (cursor) / P (pen) / H (grab)
      if (!mod && (e.key === 'v' || e.key === 'V')) {
        setTool('cursor');
        return;
      }
      if (!mod && (e.key === 'p' || e.key === 'P')) {
        setTool('pen');
        return;
      }
      if (!mod && (e.key === 'h' || e.key === 'H')) {
        setTool('grab');
        return;
      }
      // Phase 38.4: ⌘G グループ化 / ⌘Shift+G グループ解除
      if (mod && !e.shiftKey && (e.key === 'g' || e.key === 'G')) {
        if (selectedIds.length >= 2) {
          e.preventDefault();
          handleGroupSelected();
        }
        return;
      }
      if (mod && e.shiftKey && (e.key === 'g' || e.key === 'G')) {
        if (selectedIds.length > 0) {
          e.preventDefault();
          handleUngroupSelected();
        }
        return;
      }
      // Phase 39: アローキーで選択要素を移動（Shift で 10px）
      if (
        !mod &&
        (e.key === 'ArrowLeft' ||
          e.key === 'ArrowRight' ||
          e.key === 'ArrowUp' ||
          e.key === 'ArrowDown')
      ) {
        if (!activeSlide) return;
        // 背景レイヤー単独選択時は対象外（背景の x/y 編集は専用 UI 経由）
        const targetIds = selectedIds.filter((sid) => sid !== BACKGROUND_LAYER_ID);
        if (targetIds.length === 0) return;
        e.preventDefault();
        const step = e.shiftKey ? 10 : 1;
        const dx = e.key === 'ArrowLeft' ? -step : e.key === 'ArrowRight' ? step : 0;
        const dy = e.key === 'ArrowUp' ? -step : e.key === 'ArrowDown' ? step : 0;
        // 履歴: 連打区間を 1 ステップにまとめる
        if (!arrowNudgeActiveRef.current) {
          slidesHistory.pauseHistory();
          arrowNudgeActiveRef.current = true;
        }
        if (arrowNudgeTimerRef.current) clearTimeout(arrowNudgeTimerRef.current);
        arrowNudgeTimerRef.current = setTimeout(() => {
          slidesHistory.resumeHistory();
          arrowNudgeActiveRef.current = false;
          arrowNudgeTimerRef.current = null;
        }, 400);
        // バッチ更新
        const targetSet = new Set(targetIds);
        setDraftSlides((prev) =>
          prev.map((s) =>
            s.id !== activeSlide.id
              ? s
              : {
                  ...s,
                  elements: s.elements.map((el) =>
                    targetSet.has(el.id) ? { ...el, x: el.x + dx, y: el.y + dy } : el,
                  ),
                },
          ),
        );
        return;
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [
    selectedElementId,
    selectedIds,
    slidesHistory,
    handleCopySelected,
    handlePaste,
    handleDeleteElement,
    setTool,
    handleGroupSelected,
    handleUngroupSelected,
    activeSlide,
    setDraftSlides,
  ]);

  const handleExport = async (ext: 'png' | 'jpg') => {
    if (!activeSlide || !preset) return;
    try {
      await exportSlideAs(activeSlide.id, ext, `${draftTitle || 'ad-image'}.${ext}`);
      toast.success(`${ext.toUpperCase()} 出力しました`);
    } catch (e) {
      toast.error(`出力失敗: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  if (isLoading || !draftConfig) {
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    );
  }

  if (!adImage) {
    return (
      <Screen title="広告画像">
        <div className="text-center py-16 text-[var(--text-muted)]">
          見つかりません。
          <Button variant="ghost" onClick={() => navigate('/campaigns?hub=ad-images')}>
            一覧に戻る
          </Button>
        </div>
      </Screen>
    );
  }

  return (
    <Screen
      title="広告画像生成"
      backTo="/campaigns?hub=ad-images"
      actions={
        <div className="flex items-center gap-2">
          <input
            value={draftTitle}
            onChange={(e) => setDraftTitle(e.target.value)}
            onBlur={handleSaveMeta}
            className="bg-[var(--bg-input)] border border-[var(--border)] rounded-sm px-2 py-1 text-sm w-64 focus:border-[var(--border-focus)] outline-none"
            placeholder="タイトルを入力"
          />
          {activeSlide && (
            <>
              {/* Phase 32.4: ツール切替 */}
              <div className="flex items-center border border-[var(--border)] rounded-sm overflow-hidden">
                <button
                  type="button"
                  onClick={() => setTool('cursor')}
                  title="カーソル (V)"
                  className={`px-2 py-1.5 text-xs ${
                    tool === 'cursor'
                      ? 'bg-[var(--accent)]/15 text-[var(--text-primary)]'
                      : 'text-[var(--text-muted)] hover:bg-[var(--bg-hover)]'
                  }`}
                  aria-label="cursor tool"
                >
                  <MousePointer2 className="size-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setTool('pen')}
                  title="ペン (P)"
                  className={`px-2 py-1.5 text-xs ${
                    tool === 'pen'
                      ? 'bg-[var(--accent)]/15 text-[var(--text-primary)]'
                      : 'text-[var(--text-muted)] hover:bg-[var(--bg-hover)]'
                  }`}
                  aria-label="pen tool"
                >
                  <PenTool className="size-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setTool('grab')}
                  title="グラブ / 手のひら (H)"
                  className={`px-2 py-1.5 text-xs ${
                    tool === 'grab'
                      ? 'bg-[var(--accent)]/15 text-[var(--text-primary)]'
                      : 'text-[var(--text-muted)] hover:bg-[var(--bg-hover)]'
                  }`}
                  aria-label="grab tool"
                >
                  <Hand className="size-4" />
                </button>
              </div>
              {/* Phase 32.1: Undo/Redo */}
              <Button
                variant="secondary"
                size="sm"
                onClick={() => slidesHistory.undo()}
                disabled={!slidesHistory.canUndo}
                title="戻る (⌘Z)"
              >
                <Undo2 className="size-4" />
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => slidesHistory.redo()}
                disabled={!slidesHistory.canRedo}
                title="進む (⌘Y)"
              >
                <Redo2 className="size-4" />
              </Button>
            </>
          )}
          {activeSlide && (
            <Button
              variant={snapEnabled ? 'primary' : 'secondary'}
              size="sm"
              onClick={() => setSnapEnabled((v) => !v)}
              title={
                snapEnabled ? 'スナップ ON（クリックで OFF）' : 'スナップ OFF（クリックで ON）'
              }
            >
              <Magnet className="size-4 mr-1" /> スナップ
            </Button>
          )}
          {activeSlide && (
            <Button variant="primary" size="sm" onClick={() => setVariantDialogOpen(true)}>
              <Copy className="size-4 mr-1" /> バリアント生成
            </Button>
          )}
          {activeSlide && (
            <>
              <Button variant="secondary" size="sm" onClick={() => handleExport('png')}>
                <Download className="size-4 mr-1" /> PNG
              </Button>
              <Button variant="secondary" size="sm" onClick={() => handleExport('jpg')}>
                <FileImage className="size-4 mr-1" /> JPG
              </Button>
            </>
          )}
        </div>
      }
    >
      <div className="flex flex-col lg:flex-row gap-3 h-[calc(100vh-160px)]">
        {/* 左サイドパネル */}
        <div className="w-full lg:w-[340px] flex flex-col border border-[var(--border)] rounded-lg bg-[var(--bg-elevated)] overflow-hidden shrink-0">
          <div className="flex border-b border-[var(--border)] shrink-0">
            <button
              type="button"
              onClick={() => setLeftTab('generate')}
              className={`flex-1 py-3 text-sm font-medium ${
                leftTab === 'generate'
                  ? 'border-b-2 border-[var(--accent)] text-[var(--text-primary)]'
                  : 'text-[var(--text-muted)]'
              }`}
            >
              <Sparkles className="inline size-4 mr-1" /> 生成タイプ
            </button>
            <button
              type="button"
              onClick={() => setLeftTab('design')}
              className={`flex-1 py-3 text-sm font-medium ${
                leftTab === 'design'
                  ? 'border-b-2 border-[var(--accent)] text-[var(--text-primary)]'
                  : 'text-[var(--text-muted)]'
              }`}
            >
              <Wand2 className="inline size-4 mr-1" /> デザイン
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-3">
            {leftTab === 'generate' ? (
              <GenerateTab
                config={draftConfig}
                onChange={setDraftConfig}
                onGenerate={handleGenerate}
                isGenerating={generate.isPending}
              />
            ) : (
              <DesignTab
                slide={activeSlide}
                selectedElement={selectedElement}
                selectedBackgroundLayer={selectedBackgroundLayer}
                adImageId={id}
                onPatchElement={(patch) =>
                  selectedElement && handlePatchElement(selectedElement.id, patch)
                }
                onPatchSlide={handlePatchSlide}
                onPatchBackground={handlePatchBackground}
                onOptimize={handleOptimize}
                isOptimizing={optimize.isPending}
              />
            )}
          </div>

          {activeSlide && (
            <div className="border-t border-[var(--border)] p-2 shrink-0 max-h-[40%] overflow-y-auto">
              <LayerPanel
                slide={activeSlide}
                selectedId={selectedElementId}
                selectedIds={selectedIds}
                activeGroupId={activeGroupId}
                onSelect={handleSelectElementClick}
                onReorder={handleReorderLayers}
                onPatchElement={handlePatchElement}
                onDeleteElement={handleDeleteElement}
                onRenameGroup={handleRenameGroup}
                onEnterGroup={setActiveGroupId}
              />
            </div>
          )}
        </div>

        {/* メインキャンバス */}
        <div className="flex-1 flex flex-col border border-[var(--border)] rounded-lg bg-[var(--bg-root)] overflow-hidden">
          {draftSlides.length > 1 && (
            <SlideTabs slides={draftSlides} activeId={activeSlideId} onSelect={setActiveSlideId} />
          )}
          {activeSlide && preset ? (
            <>
              <div className="px-4 pt-3 flex justify-center gap-2 flex-wrap items-center">
                {/* Phase 40: 配置ボタン（要素 1 つ以上選択時） */}
                {selectedIds.filter((sid) => sid !== BACKGROUND_LAYER_ID).length >= 1 && (
                  <div className="flex items-center border border-[var(--border)] rounded-md overflow-hidden">
                    <AlignButton
                      onClick={() => handleAlign('left')}
                      title="左揃え"
                      icon={<AlignStartVertical className="size-4" />}
                    />
                    <AlignButton
                      onClick={() => handleAlign('hcenter')}
                      title="水平中央"
                      icon={<AlignCenterVertical className="size-4" />}
                    />
                    <AlignButton
                      onClick={() => handleAlign('right')}
                      title="右揃え"
                      icon={<AlignEndVertical className="size-4" />}
                    />
                    <span className="w-px h-5 bg-[var(--border)]" />
                    <AlignButton
                      onClick={() => handleAlign('top')}
                      title="上揃え"
                      icon={<AlignStartHorizontal className="size-4" />}
                    />
                    <AlignButton
                      onClick={() => handleAlign('vcenter')}
                      title="垂直中央"
                      icon={<AlignCenterHorizontal className="size-4" />}
                    />
                    <AlignButton
                      onClick={() => handleAlign('bottom')}
                      title="下揃え"
                      icon={<AlignEndHorizontal className="size-4" />}
                    />
                  </div>
                )}
                {/* Phase 40: 等間隔分配（3 まとまり以上選択時。グループは 1 まとまりで数える） */}
                {selectionUnitCount >= 3 && (
                  <div className="flex items-center border border-[var(--border)] rounded-md overflow-hidden">
                    <AlignButton
                      onClick={() => handleDistribute('horizontal')}
                      title="水平方向 等間隔"
                      icon={<AlignHorizontalSpaceAround className="size-4" />}
                    />
                    <AlignButton
                      onClick={() => handleDistribute('vertical')}
                      title="垂直方向 等間隔"
                      icon={<AlignVerticalSpaceAround className="size-4" />}
                    />
                  </div>
                )}
                {/* Phase 38.4: グループ化ボタン（複数選択中に表示） */}
                {selectedIds.length >= 2 && (
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    onClick={handleGroupSelected}
                    title="選択した要素をグループ化 (⌘G)"
                  >
                    <Folder className="size-4 mr-1" /> グループ化
                  </Button>
                )}
                {selectedIds.length > 0 &&
                  activeSlide.elements.some((e) => selectedIds.includes(e.id) && e.groupId) && (
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      onClick={handleUngroupSelected}
                      title="グループを解除 (⌘Shift+G)"
                    >
                      <FolderOpen className="size-4 mr-1" /> グループ解除
                    </Button>
                  )}
                <AddElementMenu
                  presetWidth={preset.width}
                  presetHeight={preset.height}
                  maxZIndex={Math.max(0, ...activeSlide.elements.map((e) => e.zIndex))}
                  onAdd={(el) => {
                    setDraftSlides((prev) =>
                      prev.map((s) =>
                        s.id !== activeSlide.id
                          ? s
                          : {
                              ...s,
                              elements: [...s.elements, el],
                              layerOrder: [el.id, ...(s.layerOrder ?? s.elements.map((x) => x.id))],
                            },
                      ),
                    );
                    setSelectedElementId(el.id);
                  }}
                />
              </div>
              <div className="flex-1 relative">
                <div
                  ref={canvasScrollRef}
                  className="absolute inset-0 overflow-auto"
                  style={{
                    cursor: tool === 'grab' ? 'grab' : undefined,
                  }}
                  onMouseDown={(e) => {
                    // Phase 37: グラブツール: ドラッグでスクロール
                    if (tool === 'grab' && canvasScrollRef.current) {
                      e.preventDefault();
                      const el = canvasScrollRef.current;
                      el.style.cursor = 'grabbing';
                      const startX = e.clientX;
                      const startY = e.clientY;
                      const startScrollLeft = el.scrollLeft;
                      const startScrollTop = el.scrollTop;
                      const onMove = (ev: MouseEvent) => {
                        el.scrollLeft = startScrollLeft - (ev.clientX - startX);
                        el.scrollTop = startScrollTop - (ev.clientY - startY);
                      };
                      const onUp = () => {
                        el.style.cursor = 'grab';
                        window.removeEventListener('mousemove', onMove);
                        window.removeEventListener('mouseup', onUp);
                      };
                      window.addEventListener('mousemove', onMove);
                      window.addEventListener('mouseup', onUp);
                      return;
                    }
                    // Phase 32 / 38.1: キャンバス余白クリックで選択解除
                    const tgt = e.target as HTMLElement;
                    if (tgt === e.currentTarget || tgt.dataset.canvasOuter === 'true') {
                      setSelectedElementId(null);
                    }
                  }}
                >
                  {/* Phase 38.1: min-h-full + flex で「収まる時は中央寄せ、大きい時はスクロール」を両立 */}
                  <div
                    data-canvas-outer="true"
                    className="min-h-full min-w-full p-4 flex items-center justify-center"
                  >
                    <InteractiveCanvas
                      slide={activeSlide}
                      preset={preset}
                      selectedId={selectedElementId}
                      selectedIds={selectedIds}
                      activeGroupId={activeGroupId}
                      tool={tool}
                      zoom={zoom}
                      onZoomChange={setZoom}
                      onSelectElement={handleSelectElementClick}
                      onPatchElement={handlePatchElement}
                      onPatchBackground={handlePatchBackground}
                      onDuplicateForDrag={handleDuplicateForDrag}
                      onBeginInteraction={slidesHistory.pauseHistory}
                      onEndInteraction={slidesHistory.resumeHistory}
                      onAddElement={(el) => {
                        setDraftSlides((prev) =>
                          prev.map((s) =>
                            s.id !== activeSlide.id
                              ? s
                              : {
                                  ...s,
                                  elements: [...s.elements, el],
                                  layerOrder: [
                                    el.id,
                                    ...(s.layerOrder ?? s.elements.map((x) => x.id)),
                                  ],
                                },
                          ),
                        );
                      }}
                      snapEnabled={snapEnabled}
                    />
                  </div>
                </div>
                {/* Phase 36: ズームコントロール（右下、スクロール外のラッパーに絶対配置） */}
                <div className="absolute bottom-3 right-3 z-10 flex items-center gap-1 bg-[var(--bg-elevated)] border border-[var(--border)] rounded-md shadow-md px-2 py-1">
                  <button
                    type="button"
                    onClick={() => setZoom((z) => Math.max(0.25, Number((z - 0.25).toFixed(2))))}
                    className="size-6 flex items-center justify-center text-[var(--text-muted)] hover:bg-[var(--bg-hover)] rounded-sm text-sm"
                    title="縮小"
                  >
                    −
                  </button>
                  <div className="relative">
                    <span className="text-xs font-mono w-12 text-center inline-block">
                      {Math.round(zoom * 100)}%
                    </span>
                    <select
                      value=""
                      onChange={(e) => {
                        if (e.target.value) setZoom(Number(e.target.value));
                      }}
                      className="absolute inset-0 w-full opacity-0 cursor-pointer"
                      title="ズーム倍率を選択"
                    >
                      <option value="">{Math.round(zoom * 100)}%</option>
                      {[0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2].map((z) => (
                        <option key={z} value={z}>
                          {Math.round(z * 100)}%
                        </option>
                      ))}
                    </select>
                  </div>
                  <button
                    type="button"
                    onClick={() => setZoom((z) => Math.min(2, Number((z + 0.25).toFixed(2))))}
                    className="size-6 flex items-center justify-center text-[var(--text-muted)] hover:bg-[var(--bg-hover)] rounded-sm text-sm"
                    title="拡大"
                  >
                    +
                  </button>
                  <button
                    type="button"
                    onClick={() => setZoom(1)}
                    className="text-[10px] text-[var(--text-muted)] hover:text-[var(--text-primary)] px-1"
                    title="100% に戻す"
                  >
                    100%
                  </button>
                  {/* Phase 37: 中央へ戻す */}
                  <button
                    type="button"
                    onClick={recenterView}
                    className="size-6 flex items-center justify-center text-[var(--text-muted)] hover:bg-[var(--bg-hover)] rounded-sm"
                    title="ビューを中央に戻す"
                    aria-label="recenter view"
                  >
                    <Locate className="size-4" />
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-center gap-3 text-[var(--text-muted)]">
              {generate.isPending ? (
                <>
                  <Loader2 className="size-10 animate-spin text-[var(--accent)]" />
                  <p>生成中... 最大 1 分かかります</p>
                </>
              ) : adImage?.status === 'failed' && adImage.errorMessage ? (
                <div className="max-w-xl px-6">
                  <div className="flex items-center justify-center gap-2 text-[var(--accent-red)] mb-3">
                    <AlertTriangle className="size-8" />
                    <span className="text-base font-semibold">生成に失敗しました</span>
                  </div>
                  <pre className="text-left text-[11px] bg-[var(--bg-elevated)] border border-[var(--border)] rounded-sm p-3 overflow-auto max-h-48 whitespace-pre-wrap break-words">
                    {adImage.errorMessage}
                  </pre>
                  <p className="text-xs text-[var(--text-muted)] mt-3">
                    エラー内容を確認し、設定や API
                    キーを見直してから再度「生成する」を押してください。
                  </p>
                </div>
              ) : (
                <>
                  <Sparkles className="size-10" />
                  <p>左の「生成タイプ」で設定して、生成ボタンを押してください</p>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {adImage && (
        <VariantGenerateDialog
          open={variantDialogOpen}
          onOpenChange={setVariantDialogOpen}
          master={adImage}
          onCompleted={() => {
            // 一覧画面の更新は invalidate 済み。エディタ側はマスターを保持
          }}
        />
      )}
    </Screen>
  );
}

/** Phase 40: 配置/等間隔ボタン共通の小さいアイコンボタン */
function AlignButton({
  onClick,
  title,
  icon,
}: {
  onClick: () => void;
  title: string;
  icon: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-label={title}
      className="px-2 py-1.5 text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)]"
    >
      {icon}
    </button>
  );
}
